import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

// In-feed sponsored placement. One break after every `every` cards (default 10), never before the first group.
// Fetches sequentially page-wide so each break excludes ads already shown; collapses when nothing is eligible.
export const AD_EVERY = 10;
let chain = Promise.resolve();

export function useAdRegistry() {
  return useRef([]);
}

export function isAdBreak(idx, total, every = AD_EVERY) {
  return (idx + 1) % every === 0 && idx + 1 < total;
}

const SIZE_ASPECT = { SPOTLIGHT_400x300: "400 / 300", FEATURED_728x250: "728 / 250", PREMIUM_1200x300: "1200 / 300", HOUSE_1575x1000: "1575 / 1000" };
const SIZE_MAX_W = { HOUSE_1575x1000: "max-w-[560px]", SPOTLIGHT_400x300: "max-w-[400px]", FEATURED_728x250: "max-w-[728px]" };

export default function InFeedAd({ zone, registry, geo, className = "" }) {
  const [item, setItem] = useState(null);
  useEffect(() => {
    let cancelled = false;
    chain = chain.then(async () => {
      try {
        const exclude = registry.current.slice(-3).join(","); // sliding window → never repeats the previous ad
        const params = exclude ? { zone, exclude } : { zone };
        if (geo?.state) params.state = geo.state;
        if (geo?.county) params.county = geo.county;
        if (geo?.town) params.town = geo.town;
        const r = await api.get("/public/ad-zones/next", { params });
        const it = r.data?.item;
        if (it?.kind === "AD" && !cancelled) { registry.current.push(it.ad_id); setItem(it); }
      } catch { /* collapse silently */ }
    });
    return () => { cancelled = true; };
  }, [zone, registry, geo?.state, geo?.county, geo?.town]);

  if (!item) return null;
  const onClick = async (e) => {
    e.preventDefault();
    try { await api.post(`/public/ad-zones/${item.ad_id}/click`, null, { params: { zone } }); } catch {}
    window.open(item.destination_url, "_blank", "noopener,noreferrer");
  };
  return (
    <div data-testid={`infeed-ad-${zone}`} data-ad-id={item.ad_id} data-size-slot={item.size_slot}
         className={`relative mx-auto w-full ${SIZE_MAX_W[item.size_slot] || ""} rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="absolute top-2 left-2 z-10 text-[9px] font-semibold uppercase tracking-widest bg-slate-900/80 text-white px-1.5 py-0.5 rounded">{item.label || "Sponsored"}</div>
      <a href={item.destination_url} target="_blank" rel="noopener noreferrer sponsored" onClick={onClick}
         aria-label={`${item.alt_text} — opens in a new tab`}
         className="flex items-center justify-center w-full" style={{ aspectRatio: SIZE_ASPECT[item.size_slot] || "1200 / 300" }}>
        <img src={item.creative_url} alt={item.alt_text} loading="lazy" decoding="async" className="max-w-full max-h-full w-auto h-auto object-contain block"/>
      </a>
    </div>
  );
}
